import React, { createContext, useContext, useEffect, useMemo } from "react";
import axios, { AxiosInstance, AxiosError } from "axios";
import { useAuth } from "./AuthProvider";
const BASE_URL = `${
  process.env.REACT_APP_API_URL || "http://localhost:3000"
}/api/`;

interface APIContextType {
  client: AxiosInstance;
}

const APIContext = createContext<APIContextType | undefined>(undefined);

export const useAPI = () => {
  const context = useContext(APIContext);
  if (!context) {
    throw new Error("useAPI must be used within an APIProvider");
  }
  return context;
};

interface APIProviderProps {
  children: React.ReactNode;
}

export const APIProvider: React.FC<APIProviderProps> = ({ children }) => {
  const { accessToken } = useAuth();

  const client = useMemo(() => {
    return axios.create({
      baseURL: BASE_URL,
      timeout: 0,
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });
  }, [accessToken]);

  useEffect(() => {
    let isRefreshing = false;
    let failedQueue: {
      resolve: (value: unknown) => void;
      reject: (error: unknown) => void;
    }[] = [];

    client.interceptors.request.use(
      (config) => {
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest: any = error.config;


        if (error.response && error.response.data && (error.response.data as any).message) {
          alert((error.response.data as any).message);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then((newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return client(originalRequest);
            });
          }
        }
        return Promise.reject(error);
      }
    );
  }, [accessToken]);

  return (
    <APIContext.Provider value={{ client }}>{children}</APIContext.Provider>
  );
};
